import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, ChevronRight, Mail, Phone, Building2, 
  Droplets, Shield, Gauge, Thermometer, Users, 
  Leaf, Award, FileCheck, ArrowRight, Check,
  Globe, Heart, Sparkles, Copy, ArrowUp
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const ASSETS = {
  product: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/kcd6p8mc_GFT.png",
  awards: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/5mvh14zd_Picture%201.png",
  event: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/tiuhzmdw_WhatsApp%20Image%202026-02-16%20at%2020.56.16.jpeg",
  techBg: "https://images.unsplash.com/photo-1637186209526-652da1746ff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxncmFwaGVuZSUyMGhleGFnb25hbCUyMGxhdHRpY2UlMjBzdHJ1Y3R1cmUlMjBkYXJrJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzcxMjQ2ODAwfDA&ixlib=rb-4.1.0&q=85",
  mystiLogo: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/vzk53b85_MySTI%20Color%20Logo.png",
  upmLogo: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/9s3860gy_20201128221831UPM_LOGO_New_FINAL.png",
  // Certificates
  certComstech: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/9wzvwkx2_Screenshot%202026-02-23%20at%206.29.28%E2%80%AFPM.png",
  certItex: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/fq77jbbl_Screenshot%202026-02-23%20at%206.30.38%E2%80%AFPM.png",
  certUpmFellowship: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/2jd8xcz9_Screenshot%202026-02-23%20at%206.32.05%E2%80%AFPM.png",
  certMalaysiaComm: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/g5czwrcz_Screenshot%202026-02-23%20at%206.33.19%E2%80%AFPM.png",
  certMysti: "https://customer-assets.emergentagent.com/job_lube-tech/artifacts/gqkyto22_Screenshot%202026-02-23%20at%206.33.54%E2%80%AFPM.png",
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#overview", label: "Overview" },
    { href: "#technology", label: "Technology" },
    { href: "#benefits", label: "Benefits" },
    { href: "#use", label: "How to Use" },
    { href: "#validation", label: "Validation" },
    { href: "#csr", label: "CSR" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_lube-tech/artifacts/msy7u0vh_GrafinTok.png"
              alt="GrafinTok Bottle"
              className="w-10 h-14 object-contain"
            />
            <div>
              <span className="text-lg font-bold text-white tracking-tight">GrafinTok<sup className="text-xs">®</sup></span>
              <span className="block text-xs text-gray-400">Graphene Lubricant</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-sm text-gray-300 hover:text-white transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                data-testid="nav-enquiry-btn"
                className="bg-[#D4AF37] hover:bg-[#F2D06B] text-black font-semibold rounded-full px-6 transition-all shadow-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
              >
                Enquire Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block text-gray-300 hover:text-white py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#D4AF37] hover:bg-[#F2D06B] text-black font-semibold rounded-full">
                  Enquire Now
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="top" className="hero-gradient min-h-screen pt-32 pb-20 px-6 lg:px-12" data-testid="hero-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-6">
              <span className="pill-badge px-4 py-2 rounded-full text-sm font-medium">
                Graphene-Enhanced Engine Oil Booster
              </span>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
            >
              Advanced lubrication{" "}
              <span className="text-[#D4AF37] gold-text-glow">enabled by graphene</span>
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-2xl md:text-3xl text-[#D4AF37] font-semibold mb-8 gold-text-glow"
            >
              Powered by Graphene. Driven by GrafinTok®.
            </motion.p>

            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-400 mb-8 max-w-xl"
            >
              GrafinTok® is a graphene-enhanced lubrication additive designed to support 
              reduced frictional losses and improved lubrication performance in real-world 
              operation, engineered for everyday driving and professional automotive applications.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-10">
              <a href="#contact">
                <Button 
                  data-testid="hero-enquiry-btn"
                  className="btn-primary bg-[#D4AF37] hover:bg-[#F2D06B] text-black font-semibold rounded-full px-8 py-6 text-lg shadow-[0_0_30px_rgba(212,175,55,0.3)]"
                >
                  Distributor Enquiries
                </Button>
              </a>
              <a href="#validation">
                <Button 
                  data-testid="hero-validation-btn"
                  variant="outline" 
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-8 py-6 text-lg"
                >
                  View Validation
                </Button>
              </a>
              <a href="#csr">
                <Button 
                  data-testid="hero-csr-btn"
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                >
                  Corporate Social Responsibility
                </Button>
              </a>
            </motion.div>

            {/* Trust indicators */}
            <motion.div 
              variants={fadeInUp}
              className="glass rounded-2xl p-5"
            >
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[#D4AF37] font-bold text-lg">Malaysia</p>
                  <p className="text-xs text-gray-400">Commercial Deployment</p>
                </div>
                <div>
                  <p className="text-[#D4AF37] font-bold text-lg">ITEX Gold</p>
                  <p className="text-xs text-gray-400">Medal Winner</p>
                </div>
                <div>
                  <p className="text-[#D4AF37] font-bold text-lg">UPM</p>
                  <p className="text-xs text-gray-400">Research Origin</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Product Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex justify-center"
          >
            <div className="product-glow relative">
              <img
                src={ASSETS.product}
                alt="GrafinTok Engine Oil Booster"
                className="w-full max-w-md drop-shadow-2xl"
                data-testid="hero-product-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Overview Section
const OverviewSection = () => {
  return (
    <section id="overview" className="py-24 px-6 lg:px-12 bg-[#050505]" data-testid="overview-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Overview</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">What is GrafinTok®?</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="feature-card bg-[#111] border border-white/5 p-8 rounded-2xl"
          >
            <Sparkles className="w-10 h-10 text-[#D4AF37] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-3">Graphene-Enhanced Formula</h3>
            <p className="text-gray-400">
              GrafinTok® is a graphene-enhanced lubrication additive formulated to support 
              reduced frictional losses and smoother engine operation. It is designed for 
              straightforward application within existing workshop routines and daily use.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="feature-card bg-[#111] border border-white/5 p-8 rounded-2xl"
          >
            <Globe className="w-10 h-10 text-[#D4AF37] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-3">From Research to Reality</h3>
            <p className="text-gray-400">
              GrafinTok® translates materials engineering know-how from Universiti Putra 
              Malaysia into a product designed for practical mobility contexts, where 
              reliability, explainability, and consistent application matter.
            </p>
          </motion.div>
        </div>

        {/* Product Specs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 glass rounded-2xl p-8"
        >
          <h3 className="text-lg font-semibold text-white mb-6">Product Specifications</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: "Category", value: "Lubrication Additive" },
              { label: "Compatible", value: "Petrol & Diesel Vehicles" },
              { label: "Oil Types", value: "All Synthetics" },
              { label: "Application", value: "Pour-in" },
              { label: "Positioning", value: "Premium" },
            ].map((spec, i) => (
              <div key={i} className="spec-row rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">{spec.label}</p>
                <p className="text-white font-medium">{spec.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Formulation Compatibility Section
const FormulationSection = () => {
  return (
    <section className="py-20 px-6 lg:px-12 relative overflow-hidden" data-testid="formulation-section">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-[#050505] to-[#D4AF37]/10" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Standout Card */}
          <div className="relative">
            {/* Gold border glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D4AF37] via-[#F2D06B] to-[#D4AF37] rounded-3xl blur-sm opacity-30" />
            
            <div className="relative bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-3xl p-10 md:p-14">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B08A45] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.3)]">
                <Droplets className="w-8 h-8 text-black" />
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-6 gold-text-glow">
                Formulation Compatibility & Stability
              </h2>

              <div className="space-y-6 text-left md:text-center max-w-3xl mx-auto">
                <p className="text-gray-300 text-lg leading-relaxed">
                  Engine oils contain complex additive systems. When external additives are introduced, 
                  incompatibility can result in <span className="text-white font-medium">sediment formation</span>, 
                  <span className="text-white font-medium"> precipitation</span>, or 
                  <span className="text-white font-medium"> phase separation</span>.
                </p>

                <p className="text-gray-300 text-lg leading-relaxed">
                  <span className="text-[#D4AF37] font-semibold">GrafinTok®</span> is engineered for formulation stability. 
                  In internal compatibility assessments, GrafinTok® maintained a 
                  <span className="text-white font-medium"> uniform dispersion without visible sedimentation</span> when 
                  blended with commercially available engine oils.
                </p>

                <div className="pt-4 border-t border-[#D4AF37]/20">
                  <p className="text-[#D4AF37] text-lg font-medium italic">
                    "This stability is the foundation for consistent performance in real-world lubrication environments."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Technology Section
const TechnologySection = () => {
  const techFeatures = [
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Graphene Dispersion",
      description: "Graphene is dispersed within the lubricant system to support interaction at moving interfaces during operation."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Boundary Lubrication",
      description: "Under sliding contact, graphene-based materials help support lubrication conditions at microscopic asperities, reducing direct contact."
    },
    {
      icon: <Thermometer className="w-8 h-8" />,
      title: "Thermal Management",
      description: "Graphene's thermal characteristics support heat distribution within the lubricant system under demanding conditions."
    },
  ];

  return (
    <section id="technology" className="py-24 px-6 lg:px-12 relative overflow-hidden" data-testid="technology-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${ASSETS.techBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Technology</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Explainable, conservative, evidence-aligned technology
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {techFeatures.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="feature-card bg-[#0a0a0a] border border-white/5 p-8 rounded-2xl text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
              <p className="text-gray-400">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  return (
    <section id="benefits" className="py-24 px-6 lg:px-12 bg-[#050505]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Benefits</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">What Customers Care About</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Operational Feel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="feature-card bg-[#111] border border-white/5 p-8 rounded-2xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <Gauge className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-semibold text-white">Operational Feel</h3>
            </div>
            <ul className="space-y-4">
              {[
                "Supports smoother operation through improved lubrication conditions",
                "Designed to help reduce frictional losses at high-contact zones",
                "May help reduce harshness under load (depending on engine condition)"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-400">
                  <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Protection Mindset */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="feature-card bg-[#111] border border-white/5 p-8 rounded-2xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-semibold text-white">Protection Mindset</h3>
            </div>
            <ul className="space-y-4">
              {[
                "Designed to support wear protection under typical operating conditions",
                "Suitable for high-mileage usage with proper maintenance",
                "Workshop-friendly as a professional add-on service"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-400">
                  <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Target audience */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 glass rounded-2xl p-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Users className="w-8 h-8 text-[#D4AF37]" />
            <h3 className="text-xl font-semibold text-white">Who It's For</h3>
          </div>
          <p className="text-gray-400">
            High-mileage drivers, workshops, fleets, ride-hailing communities, and motorists 
            seeking a premium "efficiency + protection" upgrade with straightforward application.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// How to Use Section
const HowToUseSection = () => {
  const steps = [
    {
      number: "01",
      title: "Add to Engine Oil",
      description: "Add GrafinTok® to engine oil following the recommended dosage guidance."
    },
    {
      number: "02",
      title: "Operate Normally",
      description: "Operate the vehicle normally; the additive disperses during use."
    },
    {
      number: "03",
      title: "Maintain & Track",
      description: "Continue standard maintenance. For fleets/workshops, track feedback consistently."
    },
  ];

  return (
    <section id="use" className="py-24 px-6 lg:px-12 bg-[#0a0a0a]" data-testid="use-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Application</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How to Use</h2>
          <p className="text-gray-400">Simple and professional application</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="relative"
            >
              <div className="feature-card bg-[#111] border border-white/5 p-8 rounded-2xl h-full">
                <span className="text-5xl font-bold text-[#D4AF37]/20 absolute top-4 right-6">
                  {step.number}
                </span>
                <h3 className="text-xl font-semibold text-white mb-3 mt-8">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/30 w-8 h-8" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Workshop guidance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 border border-[#D4AF37]/20"
        >
          <div className="flex items-center gap-4 mb-4">
            <Building2 className="w-8 h-8 text-[#D4AF37]" />
            <h3 className="text-xl font-semibold text-white">Workshop Guidance</h3>
          </div>
          <p className="text-gray-400">
            Present GrafinTok® as a premium maintenance upgrade designed to improve lubrication performance, 
            reduce engine friction, and maintain consistent operation. Provide clear usage instructions 
            and communicate product benefits based on verified performance results.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Validation Section
const ValidationSection = () => {
  const certificates = [
    {
      image: ASSETS.certComstech,
      title: "OIC-COMSTECH Best Patent Award",
      description: "Recognition for outstanding patent contribution"
    },
    {
      image: ASSETS.certItex,
      title: "ITEX 2021 Gold Medal",
      description: "International Invention & Technology Exhibition"
    },
    {
      image: ASSETS.certUpmFellowship,
      title: "Vice Chancellor Fellowship Award",
      description: "Research and Innovation Excellence at UPM"
    },
    {
      image: ASSETS.certMalaysiaComm,
      title: "Malaysia Commercialisation Year Award",
      description: "National recognition for commercialization"
    },
    {
      image: ASSETS.certMysti,
      title: "MySTI Certificate",
      description: "MOSTI certification for local R&D products"
    }
  ];

  return (
    <section id="validation" className="py-24 px-6 lg:px-12 bg-[#050505]" data-testid="validation-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Validation</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Recognition & Certification</h2>
          <p className="text-gray-400">Evidence-first, credible, internationally recognized</p>
        </motion.div>

        {/* Certificates Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {certificates.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-xl overflow-hidden hover:scale-105 transition-transform"
              >
                <div className="relative">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="w-full h-auto object-contain"
                  />
                  {/* Watermark Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span 
                      className="text-2xl md:text-3xl font-bold text-black/20 transform -rotate-30 select-none"
                      style={{ 
                        textShadow: '1px 1px 2px rgba(255,255,255,0.3)',
                        transform: 'rotate(-30deg)'
                      }}
                    >
                      GrafinTok®
                    </span>
                  </div>
                </div>
                <div className="p-3 text-center">
                  <h4 className="text-white font-medium text-sm">{cert.title}</h4>
                  <p className="text-gray-400 text-xs mt-1">{cert.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CSR Section
const CSRSection = () => {
  return (
    <section id="csr" className="py-24 px-6 lg:px-12 bg-[#050505]" data-testid="csr-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
            <Heart className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium">Corporate Social Responsibility</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Driving Sustainable Mobility
          </h2>

          {/* UPM Event Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-8 rounded-2xl overflow-hidden max-w-4xl mx-auto"
          >
            <img
              src={ASSETS.event}
              alt="UPM CSR Programme Event"
              className="w-full h-64 md:h-80 object-cover"
              data-testid="csr-photo"
            />
          </motion.div>

          <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
            GO Advanced Solutions Sdn. Bhd. and TOP 123 Sdn. Bhd. is committed to supporting sustainable mobility 
            and community development. Through GrafinTok®, we aim to reduce environmental 
            impact while serving drivers, fleets, service professionals, and the wider mobility community.
          </p>

          <div className="glass rounded-2xl p-8 text-left max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Leaf className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h4 className="text-white font-semibold mb-2">Environmental Impact</h4>
                <p className="text-gray-400 text-sm">
                  Reduced frictional losses contribute to improved fuel efficiency and lower emissions.
                </p>
              </div>
              <div>
                <Users className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h4 className="text-white font-semibold mb-2">Community Empowerment</h4>
                <p className="text-gray-400 text-sm">
                  Supporting local workshops with premium products and professional training.
                </p>
              </div>
              <div>
                <Globe className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h4 className="text-white font-semibold mb-2">Knowledge Transfer</h4>
                <p className="text-gray-400 text-sm">
                  Bridging academic research and commercial application for real-world benefit.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 px-6 lg:px-12 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase mb-3">Contact</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Get in Touch</h2>
          <p className="text-gray-400">Business enquiries and partnership opportunities</p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="glass rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">TOP 123 Sdn. Bhd.</p>
                    <p className="text-gray-400">Official distributor of GrafinTok®</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <a 
                      href="mailto:top123sdnbhd@gmail.com" 
                      className="text-[#D4AF37] hover:text-[#F2D06B] transition-colors text-lg font-medium"
                      data-testid="contact-email-link"
                    >
                      top123sdnbhd@gmail.com
                    </a>
                    <p className="text-gray-400">General enquiries</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <a 
                      href="https://wa.me/60129284883" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#D4AF37] hover:text-[#F2D06B] transition-colors text-lg font-medium"
                      data-testid="contact-whatsapp-link"
                    >
                      WhatsApp: +60 12-928 4883
                    </a>
                    <p className="text-gray-400">Quick response channel</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 px-6 lg:px-12 border-t border-white/10 bg-[#030303]" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        {/* Research Origin / Partners */}
        <div className="mb-8 pb-8 border-b border-white/5">
          <p className="text-gray-500 text-xs text-center mb-4">Research Origin & Certification</p>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
              <img 
                src={ASSETS.upmLogo}
                alt="Universiti Putra Malaysia"
                className="h-12 object-contain"
              />
              <div className="text-left">
                <p className="text-white text-xs font-medium">UPM Startup</p>
                <p className="text-gray-500 text-xs">Research & Development</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
              <img 
                src={ASSETS.mystiLogo}
                alt="MySTI Certified"
                className="h-12 object-contain"
              />
              <div className="text-left">
                <p className="text-white text-xs font-medium">MySTI Certified</p>
                <p className="text-gray-500 text-xs">Local R&D Product</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_lube-tech/artifacts/msy7u0vh_GrafinTok.png"
              alt="GrafinTok Bottle"
              className="w-8 h-12 object-contain"
            />
            <div>
              <span className="text-white font-bold">GrafinTok<sup className="text-xs">®</sup></span>
              <span className="block text-xs text-gray-500">Graphene-Enhanced Lubrication Additive</span>
            </div>
          </div>

          <div className="text-center md:text-right">
            <p className="text-gray-400 text-sm">
              © {currentYear} GO Advanced Solutions Sdn. Bhd. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs mt-1">
              GrafinTok® is a registered trademark of GO Advanced Solutions Sdn. Bhd.
            </p>
            <p className="text-gray-600 text-xs mt-1">
              Manufactured by GO Advanced Solutions Sdn. Bhd.
            </p>
            <p className="text-gray-600 text-xs">
              Authorized distributor: TOP 123 Sdn. Bhd.
            </p>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5">
          <p className="text-center text-xs text-gray-600">
            All statements are consistent with documented evidence and usage guidance. 
            Performance outcomes depend on engine condition, lubricant type, operating environment, and maintenance practices.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-14 h-14 bg-[#D4AF37] hover:bg-[#F2D06B] text-black rounded-full flex items-center justify-center shadow-[0_4px_20px_rgba(212,175,55,0.5)] hover:shadow-[0_4px_30px_rgba(212,175,55,0.7)] transition-all border-2 border-[#F2D06B]"
          data-testid="back-to-top-btn"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" strokeWidth={2.5} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />
      <HeroSection />
      <OverviewSection />
      <FormulationSection />
      <TechnologySection />
      <BenefitsSection />
      <HowToUseSection />
      <ValidationSection />
      <CSRSection />
      <ContactSection />
      <Footer />
      <BackToTop />
      <Toaster 
        position="bottom-center" 
        toastOptions={{
          style: {
            background: '#111',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
